import { IField } from "@/features/fields/types"
import { cn } from "@/utils/helpers"
import { Editor, IAllProps } from "@tinymce/tinymce-react"
import { useRef } from "react"
import { Editor as EditorType } from "tinymce"

interface ITextEditor extends IAllProps {
  readOnly?: boolean
  placeholder?: string
  fields?: IField[]
  simple?: boolean
  toolbarSticky?: boolean
}

export const TextEditor = ({
  placeholder = "Start adding content here...",
  fields,
  readOnly = false,
  simple = false,
  toolbarSticky = false,
  init,
  value,
  onEditorChange,
  ...props
}: ITextEditor) => {
  const showToolbar = !readOnly && !simple
  const editorRef = useRef<EditorType | null>(null)
  const { setup: callerSetup, ...initOverrides } = init ?? {}

  const insertField = (field: IField) => {
    const fieldValue = `{{${field.key}}}`
    editorRef.current?.insertContent(fieldValue)
  }

  const syncEditorContent = (editor: EditorType) => {
    const content = editor.getContent()
    onEditorChange?.(content, editor)
  }

  return (
    <div className={cn("cursor rounded-[10px] border bg-white")}>
      <Editor
        {...props}
        value={value || ""}
        disabled={readOnly || props.disabled}
        apiKey={process.env.NEXT_PUBLIC_TINY_API_KEY}
        onInit={(_, editor) => {
          editorRef.current = editor

          setTimeout(() => {
            editor.focus(false)
            editor.getWin().scrollTo(0, 0)
          }, 0)
        }}
        onEditorChange={onEditorChange}
        init={{
          menubar:
            readOnly || simple
              ? false
              : `edit view insert format tools ${fields ? "custom" : ""}`,
          toolbar:
            readOnly || simple
              ? false
              : "undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
          content_style: `
            body {
              font-family: Helvetica, Arial, sans-serif;
              font-size: 14px;
              ${props.disabled ? "cursor: not-allowed;" : ""}
            }
            input[type="checkbox"] {
              pointer-events: auto;
              cursor: pointer;
            }
            ${props.disabled ? "iframe { cursor: not-allowed; } html { cursor: not-allowed; }" : ""}
          `,
          placeholder,
          height: 500,
          promotion: false,
          statusbar: false,
          resize: false,
          valid_elements: "*[*]",
          extended_valid_elements: "input[checked|type|disabled|name|value]",
          ...(toolbarSticky && showToolbar
            ? {
                toolbar_sticky: true,
                ui_mode: "split" as const,
              }
            : {}),
          plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "image",
            "charmap",
            "anchor",
            "searchreplace",
            "visualblocks",
            "code",
            "fullscreen",
            "insertdatetime",
            "media",
            "table",
            "help",
            "wordcount",
            "autoresize",
          ],
          setup: (editor) => {
            if (fields) {
              fields.forEach((field) => {
                editor.ui.registry.addMenuItem(`insertfield_${field.key}`, {
                  text: field.label,
                  onAction: () => insertField(field),
                })
              })
            }

            if (!readOnly && !props.disabled) {
              editor.on("mousedown", (event) => {
                const target = event.target as HTMLInputElement | null

                if (
                  target &&
                  target.tagName === "INPUT" &&
                  target.type === "checkbox"
                ) {
                  event.preventDefault()
                }
              })

              editor.on("click", (event) => {
                const target = event.target as HTMLInputElement | null

                if (
                  !target ||
                  target.tagName !== "INPUT" ||
                  target.type !== "checkbox"
                ) {
                  return
                }

                event.preventDefault()
                event.stopPropagation()

                const win = editor.getWin()
                const scrollX = win.scrollX
                const scrollY = win.scrollY
                const nextChecked = !target.hasAttribute("checked")

                editor.undoManager.transact(() => {
                  const newInput = target.cloneNode(true) as HTMLInputElement

                  newInput.checked = nextChecked

                  if (nextChecked) {
                    newInput.setAttribute("checked", "checked")
                  } else {
                    newInput.removeAttribute("checked")
                  }

                  target.parentNode?.replaceChild(newInput, target)
                })

                editor.setDirty(true)
                editor.nodeChanged()
                syncEditorContent(editor)

                requestAnimationFrame(() => {
                  editor.getWin().scrollTo(scrollX, scrollY)
                })
              })
            }

            callerSetup?.(editor)
          },
          ...(fields
            ? {
                menu: {
                  custom: {
                    title: "Add field",
                    items: fields.map((f) => `insertfield_${f.key}`).join(" "),
                  },
                },
              }
            : {}),
          ...initOverrides,
        }}
      />
    </div>
  )
}
